import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AdminBranchProvider } from "./context/AdminBranchContext";
import { DeviceProvider, useDevice } from "./context/DeviceContext";
import { StaffProvider } from "./context/StaffContext";
import BadgePrintScreen from "./screens/BadgePrintScreen";
import ChooseScreen from "./screens/ChooseScreen";
import FindScreen from "./screens/FindScreen";
import ScanScreen from "./screens/ScanScreen";
import SetupScreen from "./screens/SetupScreen";
import TodayScreen from "./screens/TodayScreen";
import type { ReactNode } from "react";

/**
 * Default-deny routing: without a device token the only thing this app can do
 * is register itself. /badges is exempt because it is a staff tool that
 * authenticates on its own and may legitimately be used on an unregistered
 * laptop in the office.
 *
 * The two gates never stack. `/`, `/scan`, `/find` and `/today` need the *device*;
 * `/setup` and `/badges` need a *staff session*, which AdminLayout asks for in
 * place of the page. No route requires both.
 */
function RequireDevice({ children }: { children: ReactNode }) {
  const { isRegistered } = useDevice();
  if (!isRegistered) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

/**
 * The kiosk's resting state, by the device's check-in mode (D-019): the
 * chooser between `/scan` and `/find` ("choice", the default), the classic
 * badge screen ("scan" — the same screen `/scan` always shows), or the find
 * screen at a branch with no scanner ("type"). A redirect rather than rendering
 * FindScreen here, so the find screen has one address and "home" means the
 * same thing from every screen.
 */
function Home() {
  const { checkInMode } = useDevice();
  if (checkInMode === "type") return <Navigate to="/find" replace />;
  if (checkInMode === "scan") return <ScanScreen />;
  return <ChooseScreen />;
}

export default function App() {
  return (
    <DeviceProvider>
      <StaffProvider>
        <AdminBranchProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <RequireDevice>
                    <Home />
                  </RequireDevice>
                }
              />
              {/* The classic badge screen, whatever the device's mode: a
                  permanent address for a bookmark or a kiosk's start URL. */}
              <Route
                path="/scan"
                element={
                  <RequireDevice>
                    <ScanScreen />
                  </RequireDevice>
                }
              />
              <Route
                path="/find"
                element={
                  <RequireDevice>
                    <FindScreen />
                  </RequireDevice>
                }
              />
              <Route
                path="/today"
                element={
                  <RequireDevice>
                    <TodayScreen />
                  </RequireDevice>
                }
              />
              <Route path="/setup" element={<SetupScreen />} />
              <Route path="/badges" element={<BadgePrintScreen />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AdminBranchProvider>
      </StaffProvider>
    </DeviceProvider>
  );
}
